import React, { useState, useEffect, useCallback } from 'react';
import { investmentPerspectivesAPI } from '../../services/api';
import OffscreenReportRenderer from './OffscreenReportRenderer';

const REPORT_NAME = 'Investment Perspectives';

/**
 * Bulk-download the month's report for multiple brand domains at once.
 * Each selected brand's report is rendered offscreen (WYSIWYG), captured, and
 * the batch is zipped server-side via /bulk-pdf/wysiwyg.
 */
const BulkDownloadModal = ({ isOpen, onClose, isDrummondUser, period, brands }) => {
  const [selected, setSelected] = useState({});      // { domain: bool }
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState(null);

  // Rendering queue state
  const [queue, setQueue] = useState([]);            // [{ domain, label }]
  const [activeData, setActiveData] = useState(null);
  const [items, setItems] = useState([]);

  const options = [
    { domain: '_default', label: 'Drummond (default)' },
    ...(brands || []).map((b) => ({ domain: b.domain, label: b.company_name || b.domain })),
  ];

  useEffect(() => {
    if (isOpen) {
      const init = {};
      options.forEach((o) => { init[o.domain] = true; });
      setSelected(init);
      setError(null);
      setProgress('');
      setItems([]);
      setQueue([]);
      setActiveData(null);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (domain) => setSelected((p) => ({ ...p, [domain]: !p[domain] }));

  const startGenerate = async () => {
    const chosen = options.filter((o) => selected[o.domain]);
    if (chosen.length === 0) return;
    setGenerating(true);
    setError(null);
    setItems([]);
    setProgress(`Preparing ${chosen.length} report${chosen.length !== 1 ? 's' : ''}…`);
    setQueue(chosen);
  };

  // Fetch the next domain's report data and hand it to the offscreen renderer.
  useEffect(() => {
    if (!generating || queue.length === 0) return;
    const next = queue[0];
    setProgress(`Rendering ${next.label}…`);
    const brandDomain = next.domain === '_default' ? null : next.domain;
    investmentPerspectivesAPI.getReportData(period, brandDomain)
      .then((res) => {
        if (!res.success) throw new Error('load failed');
        const d = res.data;
        setActiveData({
          ...d,
          quarter: d.period,
          quarter_display: d.period_display,
          portfolio_name: REPORT_NAME,
          portfolio_code: 'INVESTMENT_PERSPECTIVES',
          __label: next.label,
        });
      })
      .catch(() => {
        // Skip this domain on failure
        setQueue((q) => q.slice(1));
      });
  }, [generating, queue, period]);

  // Offscreen capture callback — collect HTML, advance the queue.
  const handleCapture = useCallback((html) => {
    const label = activeData?.__label || 'report';
    const safe = String(label).replace(/[^\w-]+/g, '_');
    setItems((prev) => [...prev, { html_content: html, filename: `${period}_${REPORT_NAME.replace(/\s+/g, '_')}_${safe}.pdf` }]);
    setActiveData(null);
    setQueue((q) => q.slice(1));
  }, [activeData, period]);

  // When the queue drains, POST the batch and download the zip.
  useEffect(() => {
    if (!generating || queue.length !== 0 || activeData) return;
    if (items.length === 0) { setGenerating(false); return; }
    let cancelled = false;
    setProgress('Building PDFs…');
    investmentPerspectivesAPI.bulkWysiwyg(items)
      .then((response) => {
        if (cancelled) return;
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${period}_Investment_Perspectives.zip`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        setProgress('Done.');
      })
      .catch(() => setError('Error generating bulk PDFs.'))
      .finally(() => { if (!cancelled) setGenerating(false); });
    return () => { cancelled = true; };
  }, [generating, queue, activeData, items, period]);

  if (!isOpen) return null;

  return (
    <div className="publish-modal-overlay" onClick={() => !generating && onClose()}>
      <div className="publish-modal" onClick={(e) => e.stopPropagation()}>
        <div className="publish-modal-header">
          <h3>Bulk Download</h3>
          <button className="publish-modal-close" onClick={() => !generating && onClose()}>&times;</button>
        </div>
        <div className="publish-modal-body">
          <p className="publish-info">Select which brands to generate the {period} report for.</p>
          {options.map((o) => (
            <label key={o.domain} className="publish-portfolio-row">
              <input type="checkbox" checked={!!selected[o.domain]} onChange={() => toggle(o.domain)} disabled={generating} />
              <span className="publish-portfolio-name">{o.label}</span>
            </label>
          ))}
          {progress && <p className="publish-info" style={{ marginTop: '0.75rem' }}>{progress}</p>}
          {error && <div className="error-banner">{error}</div>}
        </div>
        <div className="publish-modal-footer">
          <button className="btn-cancel" onClick={() => onClose()} disabled={generating}>Close</button>
          <button className="btn-save-publish" onClick={startGenerate} disabled={generating}>
            {generating ? 'Generating…' : 'Generate ZIP'}
          </button>
        </div>
      </div>

      {activeData && <OffscreenReportRenderer data={activeData} onCapture={handleCapture} />}
    </div>
  );
};

export default BulkDownloadModal;
